<template>
  <div class="container page info-page">
    <van-nav-bar :title="$t('setting.base_setting')" class="nav-bar" :border="false">
      <template #left>
        <van-icon name="arrow-left" @click="back()" />
      </template>
    </van-nav-bar>

    <div class="main-content">
      <div class="info-card">
        <div class="item van-hairline--bottom" @click="onAvatarClick">
          <span class="label">{{ $t('setting.avatar') }}</span>
          <div class="right">
            <img class="avatar-img" :src="avatarSrc" alt="avatar" @error="onAvatarError" />
            <van-icon name="arrow" />
          </div>
        </div>
        <div class="item van-hairline--bottom" @click="toSetName()">
          <span class="label">{{ $t('setting.real_name') }}</span>
          <div class="right">
            <span class="desc">{{ userInfo.name || $t('setting.no_setting') }}</span>
            <van-icon name="arrow" />
          </div>
        </div>
        <div class="item" @click="toSetSex()">
          <span class="label">{{ $t('setting.sex') }}</span>
          <div class="right">
            <span class="desc">{{ sexText }}</span>
            <van-icon name="arrow" />
          </div>
        </div>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/jpg,image/webp"
      class="file-input"
      @change="onFileChange"
    />

    <van-popup v-model="showAction" position="bottom" round get-container="body">
      <div class="avatar-actions">
        <div class="action-item" @click="triggerUpload">上傳照片</div>
        <div class="action-item van-hairline--top" @click="openAvatarPicker">選擇頭像</div>
        <div class="action-gap"></div>
        <div class="action-cancel" @click="showAction = false">取消</div>
      </div>
    </van-popup>

    <van-popup v-model="showPicker" round position="bottom" get-container="body" :style="{ height: '62%' }">
      <div class="avatar-picker">
        <div class="picker-header van-hairline--bottom">
          <van-icon name="cross" @click="showPicker = false" />
          <span class="picker-title">{{ $t('setting.choose_avatar') }}</span>
          <span class="picker-ok" @click="saveSelectedAvatar">{{ $t('setting.ok') }}</span>
        </div>
        <div class="picker-grid">
          <img
            v-for="item in avatarList"
            :key="item"
            class="picker-item"
            :class="{ active: selectedAvatar === item }"
            :src="item"
            alt=""
            @click="selectedAvatar = item"
          />
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { DEFAULT_AVATAR, resolveAvatarUrl, isBrokenRemoteAvatar, isStoredDataUrl } from '@/utils/avatar'

const AVATAR_COUNT = 24

function buildAvatarList() {
  return Array.from({ length: AVATAR_COUNT }, (_, i) => `/img/avatars/${i + 1}.svg`)
}

export default {
  data() {
    return {
      userInfo: {},
      avatarSrc: DEFAULT_AVATAR,
      showAction: false,
      showPicker: false,
      selectedAvatar: '',
      uploading: false,
      avatarList: buildAvatarList()
    }
  },
  computed: {
    sexText() {
      const sex = String(this.userInfo.sex || '0')
      if (sex === '1') return this.$t('setting.man')
      if (sex === '2') return this.$t('setting.female')
      return this.$t('setting.unkown')
    }
  },
  methods: {
    ...mapMutations(['setUserInfoValue']),
    back() {
      return window.history.back()
    },
    toSetName() {
      this.$router.push({ path: '/Setname' })
    },
    toSetSex() {
      this.$router.push({ path: '/Setsex' })
    },
    onAvatarError() {
      this.avatarSrc = DEFAULT_AVATAR
    },
    updateAvatarSrc(url) {
      this.avatarSrc = resolveAvatarUrl(url)
    },
    resolvePickerAvatar(url) {
      if (url && this.avatarList.includes(url)) return url
      if (url && !isBrokenRemoteAvatar(url) && !isStoredDataUrl(url)) return url
      return this.avatarList[0]
    },
    onAvatarClick() {
      this.showAction = true
    },
    triggerUpload() {
      this.showAction = false
      this.$nextTick(() => {
        this.$refs.fileInput.click()
      })
    },
    openAvatarPicker() {
      this.showAction = false
      this.selectedAvatar = this.resolvePickerAvatar(this.userInfo.header_img)
      this.showPicker = true
    },
    compressImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (event) => {
          const img = new Image()
          img.onload = () => {
            const maxWidth = 360
            let width = img.width
            let height = img.height
            if (width > maxWidth) {
              height = Math.round(height * maxWidth / width)
              width = maxWidth
            }
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)
            resolve(canvas.toDataURL('image/jpeg', 0.75))
          }
          img.onerror = reject
          img.src = event.target.result
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!file.type.startsWith('image/')) {
        this.$toast.fail('請選擇圖片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.fail('圖片大小不能超過 5MB')
        return
      }
      this.uploading = true
      this.$toast.loading({ message: '上傳中...', forbidClick: true, duration: 0 })
      this.compressImage(file)
        .then((dataUrl) => {
          this.saveAvatar(dataUrl)
        })
        .catch(() => {
          this.uploading = false
          this.$toast.clear()
          this.$toast.fail('圖片處理失敗，請重試')
        })
    },
    saveSelectedAvatar() {
      if (!this.selectedAvatar) {
        this.$toast.fail(this.$t('setting.choose_avatar'))
        return
      }
      this.saveAvatar(this.selectedAvatar)
      this.showPicker = false
    },
    saveAvatar(headerImg) {
      if (headerImg && headerImg.startsWith('data:image/')) {
        this.avatarSrc = headerImg
      } else {
        this.updateAvatarSrc(headerImg)
      }
      this.$http({
        method: 'post',
        data: { header_img: headerImg },
        url: 'user_header_img'
      }).then(res => {
        this.uploading = false
        this.$toast.clear()
        if (res.code === 200) {
          this.$toast.success(res.msg || '頭像更新成功')
          this.getUserInfo()
        } else if (res.code === 401) {
          this.$toast.fail(res.msg || '上傳失敗')
        } else {
          this.$toast.fail(res.msg || '上傳失敗')
        }
      }).catch(() => {
        this.uploading = false
        this.$toast.clear()
        this.$toast.fail('上傳失敗，請稍後重試')
      })
    },
    getUserInfo() {
      this.$http({ method: 'get', url: 'user_info' }).then(res => {
        if (res.code === 200) {
          this.userInfo = res.data
          this.updateAvatarSrc(res.data.header_img)
          this.setUserInfoValue(res.data)
        } else if (res.code === 401) {
          this.$toast(res.msg)
        }
      })
    }
  },
  activated() {
    if (localStorage.getItem('token')) {
      this.getUserInfo()
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.push({ path: '/Login' })
    } else {
      this.getUserInfo()
    }
  }
}
</script>

<style lang="less" scoped>
@import '../../assets/css/base.css';

.info-page {
  background: #f2f2f5;
}

.main-content {
  padding: 0.75rem;
  background: #f2f2f5;
}

.info-card {
  background: #fff;
  border-radius: 0.625rem;
  overflow: hidden;
  box-shadow: 0 0.13333rem 0.2rem rgba(0, 0, 0, 0.04);
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 3.25rem;
  padding: 0.875rem;
  box-sizing: border-box;
}

.label {
  font-size: 0.9375rem;
  color: #333;
}

.right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.avatar-img {
  width: 2.9375rem;
  height: 2.9375rem;
  border-radius: 50%;
  object-fit: cover;
  background: #f5f5f5;
}

.desc {
  font-size: 0.9375rem;
  color: #999;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-input {
  display: none;
}

.avatar-actions {
  background: #fff;
  padding-bottom: env(safe-area-inset-bottom);
}

.action-item,
.action-cancel {
  height: 3.25rem;
  line-height: 3.25rem;
  text-align: center;
  font-size: 1rem;
  color: #333;
  background: #fff;
}

.action-item:active,
.action-cancel:active {
  background: #f5f5f5;
}

.action-gap {
  height: 0.5rem;
  background: #f2f2f5;
}

.action-cancel {
  font-weight: 500;
}

.avatar-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  flex-shrink: 0;

  .van-icon {
    font-size: 1.125rem;
    color: #999;
  }

  .picker-title {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
  }

  .picker-ok {
    font-size: 0.9375rem;
    color: #8056e3;
    font-weight: 600;
  }
}

.picker-grid {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  -webkit-overflow-scrolling: touch;
}

.picker-item {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  object-fit: cover;
  border: 0.125rem solid transparent;
  box-sizing: border-box;

  &.active {
    border-color: #8056e3;
    box-shadow: 0 0 0 0.125rem rgba(128, 86, 227, 0.25);
  }
}
</style>

<style lang="less">
.van-action-sheet__item,
.van-action-sheet__cancel,
.van-action-sheet__name {
  color: #333 !important;
}
</style>
